import { Action, PayloadAction } from '@reduxjs/toolkit';
import { PermissionChangePayload, UserId } from 'features/users/types';
import { call, put, select } from 'redux-saga/effects';
import { hasPermissionsSaga } from 'features/permissions/sagas/hasPermissionsSaga';
import { PermissionTypes } from 'features/permissions/types';
import { selectLocalUserId, userLeft } from 'features/users/usersSlice';
import {
  broadcastSetupEnded,
  selectIsBroadcastSetupActive,
  selectRoomId,
} from 'features/room/roomSlice';
import { eventBus } from 'utils/eventBus';
import { RTCClient } from 'utils/webrtc';
import { VirtualBackgroundManager } from 'utils/webrtc/virtual-background/VirtualBackgroundManager';
import { virtualBackgroundCleared } from 'features/virtual-backgrounds/virtualBackgroundsSlice';
import { selectActiveVirtualBackground } from 'features/virtual-backgrounds/selectors';
import { SelectedVirtualBackground } from 'features/virtual-backgrounds/types';
import { onBroadcastEndSaga } from 'features/layout/sagas/onBroadcastEndSaga';
import { closeModal, selectActiveModalName } from 'features/modal/modalSlice';
import { ModalName } from 'features/modal/types';
import { selectNotificationsByScope } from 'features/notifications/notificationsSlice';
import { closeActionNotifications } from 'features/notifications/thunks/closeActionNotifications';
import { NotificationId } from 'features/notifications/types';

export function* onBroadcastPermissionRevokedSaga({
  payload,
}: PayloadAction<PermissionChangePayload>) {
  const { id } = payload;

  const localUserId: UserId = yield select(selectLocalUserId);

  if (localUserId === id) {
    const stillHasAudioAccess: boolean = yield call(
      hasPermissionsSaga,
      PermissionTypes.broadcastAudio
    );

    const stillHasVideoAccess: boolean = yield call(
      hasPermissionsSaga,
      PermissionTypes.broadcastVideo
    );

    yield call(
      eventBus.sendMessage,
      'permissionsChanged',
      {},
      {
        broadcast: stillHasAudioAccess || stillHasVideoAccess, // For backward compatibility
        broadcastAudio: stillHasAudioAccess,
        broadcastVideo: stillHasVideoAccess,
      }
    );

    // this is the most reliable place to close the settings modal on permission change
    const activeModal: ModalName = yield select(selectActiveModalName);
    if (activeModal === 'roomSettings') {
      yield put(closeModal('roomSettings'));
    }

    const selectedVB: SelectedVirtualBackground = yield select(selectActiveVirtualBackground);
    if (!stillHasVideoAccess && selectedVB.value) {
      yield put(virtualBackgroundCleared());
      yield call(VirtualBackgroundManager.destroy);
    }

    // stop the broadcast setup if any
    const isBroadcastSetupActive: boolean = yield select(selectIsBroadcastSetupActive);
    if (isBroadcastSetupActive) {
      yield put(broadcastSetupEnded());
      yield call(RTCClient.publishingFeed.hangup);
      return;
    }

    yield call(RTCClient.publishingFeed.hangup);

    // close broadcast related notifications if any
    const broadcastRelatedNotifications: NotificationId[] = yield select(
      selectNotificationsByScope,
      'broadcast'
    );

    const cleanupNotificationsThunk: Action = yield call(
      closeActionNotifications,
      broadcastRelatedNotifications
    );
    yield put(cleanupNotificationsThunk);
  }

  const roomId: string = yield select(selectRoomId);

  yield call(onBroadcastEndSaga, userLeft([{ userId: id, roomId }]));
}
